import { Link, useLocation, useNavigate } from "react-router-dom";
import { ShoppingBag, Gem, User, LogOut, LayoutDashboard, Package, Crown, Gift } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { SearchBar } from "./SearchBar";
import { Editable } from "./edit/Editable";
import { useContent } from "../context/ContentContext";
import { useEffect, useState } from "react";
import api from "../lib/api";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "./ui/dropdown-menu";

const SoonPill = () => <span className="ml-1 text-[9px] font-bold uppercase tracking-wide bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full align-middle">Soon</span>;

export default function Header() {
  const { user, logout } = useAuth();
  const { foodCount, setDrawerOpen, comingSoon, publicBrands } = useCart();
  const SECTIONS = publicBrands.map((b) => ({ slug: b.slug, label: b.name, id: b.id }));
  const location = useLocation();
  const navigate = useNavigate();
  const isJewelry = location.pathname.startsWith("/jewelry");
  const { editMode } = useContent();
  const [customPages, setCustomPages] = useState([]);
  useEffect(() => { api.get("/cms/pages").then(({ data }) => setCustomPages(data.filter((p) => p.show_in_nav))).catch(() => {}); }, []);

  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl" style={{ background: "rgba(255,253,248,0.82)", borderBottom: "1px solid var(--ut-line)" }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
        <Link to="/" data-testid="header-logo" className="flex items-center shrink-0">
          <img src="/logo-horizontal.webp" alt="Urban Turban by VeerGi" width="640" height="219" className="h-9 sm:h-10 w-auto" />
        </Link>

        <nav className="hidden md:flex items-center gap-1" data-testid="section-switcher">
          {SECTIONS.map((s) => (
            <Link key={s.slug} to={`/store/${s.slug}`}
              data-testid={`nav-${s.slug}`} onClick={(e) => { if (editMode) e.preventDefault(); }}
              className={`px-3 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${location.pathname === `/store/${s.slug}` ? "bg-[#E04F33] text-white" : "text-neutral-600 hover:bg-neutral-100"}`}>
              <Editable k={`nav.${s.slug}`} fallback={s.label} label={`Menu label: ${s.label}`} />{comingSoon?.[s.id] && <SoonPill />}
            </Link>
          ))}
          {customPages.map((p) => (
            <Link key={p.id} to={`/p/${p.slug}`} data-testid={`nav-page-${p.slug}`}
              className={`px-3 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${location.pathname === `/p/${p.slug}` ? "bg-[#E04F33] text-white" : "text-neutral-600 hover:bg-neutral-100"}`}>{p.title}</Link>
          ))}
          <Link to="/reservations" data-testid="nav-reservations"
            className={`px-3 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${location.pathname === "/reservations" ? "bg-[#E04F33] text-white" : "text-neutral-600 hover:bg-neutral-100"}`}>
            <Editable k="nav.reservations" fallback="Reservations" label="Menu label: Reservations" />
          </Link>
          <Link to="/our-story" data-testid="nav-our-story"
            className={`px-3 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${location.pathname === "/our-story" || location.pathname === "/story" ? "bg-[#E04F33] text-white" : "text-neutral-600 hover:bg-neutral-100"}`}>
            <Editable k="nav.our_story" fallback="Our Story" label="Menu label: Our Story" />
          </Link>
          <a href="https://regaljewels.com" target="_blank" rel="noopener noreferrer" data-testid="nav-jewelry"
            className="px-3 py-1.5 rounded-none text-sm font-semibold border transition-colors ml-2 border-neutral-300 text-neutral-700 hover:border-black whitespace-nowrap">
            <span className="inline-flex items-center gap-1.5"><Gem size={13} aria-hidden="true" /> Urban Gold & Diamonds</span>
          </a>
        </nav>

        <div className="flex items-center gap-2">
          {!isJewelry && <SearchBar />}
          {!isJewelry && (
            <button data-testid="food-cart-button" onClick={() => setDrawerOpen(true)}
              className="relative p-2.5 rounded-full bg-neutral-100 hover:bg-neutral-200 transition-colors">
              <ShoppingBag size={18} aria-hidden="true" />
              {foodCount > 0 && (
                <span data-testid="food-cart-count" className="absolute -top-1 -right-1 bg-[#E04F33] text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">{foodCount}</span>
              )}
            </button>
          )}

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu-button" className="flex items-center gap-2 p-1 rounded-full hover:bg-neutral-100 transition-colors">
                  {user.is_member && (
                    <span data-testid="club-badge" className="inline-flex items-center gap-1 bg-amber-400 text-black text-[10px] font-extrabold px-2 py-0.5 rounded-full"><Crown size={10} /> CLUB</span>
                  )}
                  {user.picture ? (
                    <img src={user.picture} alt={user.name} width="32" height="32" className="w-8 h-8 rounded-full" />
                  ) : (
                    <span className="w-8 h-8 rounded-full bg-neutral-200 flex items-center justify-center"><User size={16} /></span>
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <div className="px-2 py-1.5 text-sm font-semibold truncate">{user.name}</div>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-my-orders" onClick={() => navigate("/orders")}>
                  <Package size={15} className="mr-2" /> My Orders
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-club" onClick={() => navigate("/club")}>
                  <Crown size={15} className="mr-2 text-amber-500" /> Urban Club
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-referrals" onClick={() => navigate("/referrals")}>
                  <Gift size={15} className="mr-2 text-[#E04F33]" /> Refer & Earn
                </DropdownMenuItem>
                {(user.role === "owner" || user.role === "staff") && (
                  <DropdownMenuItem data-testid="menu-dashboard" onClick={() => navigate("/dashboard")}>
                    <LayoutDashboard size={15} className="mr-2" /> Dashboard
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-logout" onClick={logout}>
                  <LogOut size={15} className="mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </div>
      </div>

      <nav className="md:hidden flex items-center gap-1 px-3 pb-2 overflow-x-auto" data-testid="section-switcher-mobile">
        {SECTIONS.map((s) => (
          <Link key={s.slug} to={`/store/${s.slug}`}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap ${location.pathname === `/store/${s.slug}` ? "bg-[#E04F33] text-white" : "bg-neutral-100 text-neutral-600"}`}>
            <Editable k={`nav.${s.slug}`} fallback={s.label} />{comingSoon?.[s.id] && <SoonPill />}
          </Link>
        ))}
        {customPages.map((p) => (
          <Link key={p.id} to={`/p/${p.slug}`} className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap ${location.pathname === `/p/${p.slug}` ? "bg-[#E04F33] text-white" : "bg-neutral-100 text-neutral-600"}`}>{p.title}</Link>
        ))}
        <Link to="/reservations" data-testid="nav-reservations-mobile"
          className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap ${location.pathname === "/reservations" ? "bg-[#E04F33] text-white" : "bg-neutral-100 text-neutral-600"}`}>
          Reservations
        </Link>
        <a href="https://regaljewels.com" target="_blank" rel="noopener noreferrer"
          className="px-3 py-1.5 text-xs font-semibold whitespace-nowrap border border-neutral-300 text-neutral-700">
          Urban Gold & Diamonds
        </a>
      </nav>
    </header>
  );
}
